import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  FileText,
  Printer,
  AlertTriangle,
  Calendar,
  Clock,
  User,
  Home
} from 'lucide-react';
import { toast } from 'sonner';
import { escapeDeep, escapeHtml } from '../lib/escapeHtml';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EvictionForm = ({ resident, onEvictionComplete }) => {
  const { user } = useAuth();
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [evictionData, setEvictionData] = useState({
    eviction_reason: '',
    eviction_details: '',
    eviction_notes: ''
  });
  const [formData, setFormData] = useState(null);

  const evictionReasons = [
    { value: 'rule_violation', label: 'Rule Violation' },
    { value: 'disruptive_behavior', label: 'Disruptive Behavior' },
    { value: 'safety_concerns', label: 'Safety Concerns' },
    { value: 'substance_abuse', label: 'Substance Abuse' },
    { value: 'aggressive_behavior', label: 'Aggressive Behavior' },
    { value: 'theft', label: 'Theft' },
    { value: 'property_damage', label: 'Property Damage' },
    { value: 'curfew_violation', label: 'Curfew Violation' },
    { value: 'non_compliance', label: 'Non-Compliance' },
    { value: 'criminal_activity', label: 'Criminal Activity' }
  ];

  const generateEvictionForm = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/residents/${resident.id}/eviction-form`);
      setFormData(response.data);
      return response.data;
    } catch (error) {
      console.error('Error generating eviction form:', error);
      toast.error('Failed to generate eviction form');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const processEviction = async () => {
    if (!evictionData.eviction_reason || !evictionData.eviction_details) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      
      // Process the eviction
      await axios.put(`${API}/residents/${resident.id}/evict`, evictionData);
      
      // Generate the form data
      const formData = await generateEvictionForm();
      if (formData) {
        // Print the form
        printEvictionForm(formData);
        toast.success('Resident evicted and form generated');
        setShowDialog(false);
        if (onEvictionComplete) {
          onEvictionComplete();
        }
      }
    } catch (error) {
      console.error('Error processing eviction:', error);
      toast.error('Failed to process eviction');
    } finally {
      setLoading(false);
    }
  };

  const printEvictionForm = (rawData) => {
    const data = escapeDeep(rawData);
    const printWindow = window.open('', '_blank');
    const reasonLabel = escapeHtml(evictionReasons.find(r => r.value === evictionData.eviction_reason)?.label || evictionData.eviction_reason);
    const evictionDetails = escapeHtml(evictionData.eviction_details || 'No additional details provided.');
    const evictionNotes = escapeHtml(evictionData.eviction_notes || '');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Eviction Notice - ${data.resident.first_name} ${data.resident.last_name}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            line-height: 1.4;
            max-width: 8.5in;
            margin: 0 auto;
            padding: 0.5in;
            color: #000;
            font-size: 12px;
          }
          .header {
            text-align: center;
            border-bottom: 3px solid #dc2626;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          .header h1 {
            color: #dc2626;
            margin: 0;
            font-size: 20px;
            font-weight: bold;
          }
          .header h2 {
            margin: 5px 0;
            font-size: 16px;
            color: #333;
          }
          .form-section {
            margin-bottom: 15px;
            page-break-inside: avoid;
          }
          .form-section h3 {
            color: #dc2626;
            font-size: 14px;
            margin-bottom: 8px;
            border-bottom: 1px solid #ccc;
            padding-bottom: 2px;
          }
          .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
            margin-bottom: 10px;
          }
          .info-item {
            margin-bottom: 3px;
          }
          .info-label {
            font-weight: bold;
            color: #555;
          }
          .info-value {
            color: #333;
            margin-left: 10px;
          }
          .legal-notice {
            background-color: #fef2f2;
            border: 3px solid #dc2626;
            border-radius: 8px;
            padding: 15px;
            margin: 15px 0;
            text-align: center;
          }
          .legal-notice h3 {
            color: #dc2626;
            margin-top: 0;
            margin-bottom: 10px;
            font-size: 16px;
            font-weight: bold;
          }
          .legal-notice .legal-text {
            font-size: 13px;
            font-weight: bold;
            line-height: 1.5;
            margin-bottom: 10px;
          }
          .warning-box {
            background-color: #fff3cd;
            border: 2px solid #ffc107;
            border-radius: 6px;
            padding: 10px;
            margin: 10px 0;
          }
          .warning-box ul {
            margin: 5px 0;
            padding-left: 15px;
          }
          .warning-box li {
            margin: 3px 0;
            font-size: 11px;
          }
          .signature-section {
            margin-top: 20px;
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 20px;
          }
          .signature-box {
            border-top: 1px solid #333;
            padding-top: 5px;
            text-align: center;
          }
          .signature-box .title {
            font-weight: bold;
            font-size: 11px;
            margin-bottom: 5px;
          }
          .signature-box .subtitle {
            font-size: 9px;
            color: #666;
            margin-bottom: 25px;
          }
          .signature-box .date-line {
            font-size: 10px;
            margin-top: 5px;
          }
          .date-time {
            text-align: right;
            font-size: 10px;
            color: #666;
            margin-top: 15px;
          }
          .compact-list {
            margin: 5px 0;
            padding-left: 15px;
          }
          .compact-list li {
            margin: 2px 0;
            font-size: 11px;
          }
          @media print {
            body { 
              margin: 0.3in; 
              padding: 0;
              font-size: 11px;
            }
            .no-print { display: none; }
            .legal-notice {
              page-break-inside: avoid;
            }
            .signature-section {
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${data.form_info.form_type}</h1>
          <h2>${data.form_info.shelter_name}</h2>
        </div>

        <div class="form-section">
          <h3>Resident Information</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Name:</span>
              <span class="info-value">${data.resident.first_name} ${data.resident.last_name}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Room Assignment:</span>
              <span class="info-value">${data.resident.room}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Intake Date:</span>
              <span class="info-value">${data.resident.intake_date}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Days Stayed:</span>
              <span class="info-value">${data.resident.stay_days} days</span>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3>Eviction Details</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Eviction Date:</span>
              <span class="info-value">${data.eviction.date}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Eviction Time:</span>
              <span class="info-value">${data.eviction.time}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Reason:</span>
              <span class="info-value">${reasonLabel}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Staff Member:</span>
              <span class="info-value">${data.eviction.staff_name}</span>
            </div>
          </div>
          <div class="info-item" style="margin-top: 15px;">
            <span class="info-label">Details:</span>
            <div style="margin-top: 10px; padding: 10px; background: white; border: 1px solid #ddd; border-radius: 4px;">
              ${evictionDetails}
            </div>
          </div>
          ${evictionNotes ? `
          <div class="info-item" style="margin-top: 15px;">
            <span class="info-label">Additional Notes:</span>
            <div style="margin-top: 10px; padding: 10px; background: white; border: 1px solid #ddd; border-radius: 4px;">
              ${evictionNotes}
            </div>
          </div>
          ` : ''}
        </div>

        <div class="legal-notice">
          <h3>🚨 OFFICIAL TRESPASS NOTICE 🚨</h3>
          <div class="legal-text">
            YOU ARE HEREBY BANNED FROM ALL PROPERTY AND SERVICES OF<br>
            <strong>GOOD SAMARITAN MINISTRIES</strong>
          </div>
          <div class="legal-text">
            IF YOU RETURN TO THIS PROPERTY FOR ANY REASON,<br>
            THE POLICE WILL BE CALLED AND YOU WILL BE ARRESTED FOR TRESPASSING
          </div>
          <div class="legal-text">
            <strong>SUPERVISOR APPROVAL REQUIRED BEFORE ANY RE-ENTRY</strong>
          </div>
        </div>

        <div class="warning-box">
          <h3>⚠️ IMPORTANT NOTICE ⚠️</h3>
          <ul class="compact-list">
            <li><strong>You are prohibited from Good Samaritan Ministries property for ${data.important_dates.days_restricted} days</strong></li>
            <li><strong>Earliest possible return date: ${data.important_dates.readmission_eligible}</strong></li>
            <li><strong>Personal belongings must be picked up by: ${data.important_dates.pickup_deadline}</strong></li>
            <li><strong>SUPERVISOR APPROVAL REQUIRED</strong> - You cannot return without written approval from facility supervisor</li>
            <li><strong>TRESPASSING WARNING:</strong> Returning without approval will result in immediate arrest</li>
            <li>Belongings not collected within 24 hours will be disposed of or donated</li>
          </ul>
        </div>

        <div class="form-section">
          <h3>Next Steps</h3>
          <ul class="compact-list">
            <li>Collect all personal belongings immediately</li>
            <li>You have until <strong>${data.important_dates.pickup_deadline}</strong> to retrieve your items</li>
            <li>Contact facility supervisor for re-entry consideration after <strong>${data.important_dates.readmission_eligible}</strong></li>
            <li>Contact case management for housing assistance referrals</li>
            <li>Emergency shelter information available at front desk</li>
          </ul>
        </div>

        <div class="signature-section">
          <div class="signature-box">
            <div class="title">RESIDENT SIGNATURE</div>
            <div class="subtitle">I acknowledge receipt of this trespass notice</div>
            <div class="date-line">Date: _________________</div>
          </div>
          <div class="signature-box">
            <div class="title">STAFF SIGNATURE</div>
            <div class="subtitle">${data.eviction.staff_name}</div>
            <div class="date-line">Date: ${data.eviction.date}</div>
          </div>
          <div class="signature-box">
            <div class="title">POLICE SIGNATURE</div>
            <div class="subtitle">Officer Badge #: ___________</div>
            <div class="date-line">Date: _________________</div>
          </div>
        </div>

        <div class="date-time">
          Form generated: ${data.form_info.generated_at}
        </div>

        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() { window.close(); }, 1000);
          }
        </script>
      </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className="text-red-600 border-red-600 hover:bg-red-50"
        >
          <FileText className="h-4 w-4 mr-2" />
          Evict & Print Form
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-red-700 flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5" />
            <span>Evict Resident - {resident.first_name} {resident.last_name}</span>
          </DialogTitle>
          <DialogDescription>
            Process eviction and generate printable eviction form. Resident will be prohibited from staying for 30 days.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-semibold text-red-800 mb-2">Eviction Policy Reminder:</h4>
            <ul className="text-sm text-red-700 space-y-1">
              <li>• Resident prohibited from shelter for 30 days</li>
              <li>• No admin approval required to return after 30 days</li>
              <li>• Further incidents will result in permanent ban</li>
              <li>• Belongings must be picked up within 24 hours</li>
            </ul>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Eviction Reason *</label>
              <Select 
                value={evictionData.eviction_reason} 
                onValueChange={(value) => setEvictionData({...evictionData, eviction_reason: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select eviction reason" />
                </SelectTrigger>
                <SelectContent>
                  {evictionReasons.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Incident Details *</label>
              <Textarea
                required
                value={evictionData.eviction_details}
                onChange={(e) => setEvictionData({...evictionData, eviction_details: e.target.value})}
                placeholder="Describe the incident that led to eviction..."
                rows={4}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Additional Notes (Optional)</label>
              <Textarea
                value={evictionData.eviction_notes}
                onChange={(e) => setEvictionData({...evictionData, eviction_notes: e.target.value})}
                placeholder="Any additional notes or context..."
                rows={2}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setShowDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={processEviction}
              disabled={loading || !evictionData.eviction_reason || !evictionData.eviction_details}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Processing...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Printer className="h-4 w-4" />
                  <span>Evict & Print Form</span>
                </div>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EvictionForm;